import React, { createContext, useContext, useEffect, useState } from 'react';
import { Select } from 'antd';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { AppContext } from 'context/AppContext';
import { getHosts } from 'utils/apis/host';
import { resolvePreferredHostId, setMarketplaceSelectedHostId } from '../../../core/selectedHost';

const OwnerReportingHostContext = createContext({
  hostId: undefined,
  setHostId: () => {},
  hostOptions: [],
  hostsLoading: true,
  isAdmin: false,
});

const OwnerReportingHostProviderInner = ({ children, location, history }) => {
  const { getUserHosts, checkIsAdmin } = useContext(AppContext);
  const allowedHostIds = (getUserHosts && getUserHosts()) || [];
  const isAdmin = !!(checkIsAdmin && checkIsAdmin());

  const [hostId, setHostIdState] = useState(undefined);
  const [hostOptions, setHostOptions] = useState([]);
  const [hostsLoading, setHostsLoading] = useState(true);

  const queryHostId = (() => {
    const parsed = queryString.parse((location && location.search) || '');
    return parsed.hostId || undefined;
  })();

  const setHostId = nextHostId => {
    const normalized = nextHostId ? String(nextHostId) : undefined;
    setHostIdState(normalized);
    setMarketplaceSelectedHostId(normalized);
    if (history && location) {
      const parsed = queryString.parse(location.search || '');
      if (String(parsed.hostId || '') === String(normalized || '')) return;
      const nextSearch = queryString.stringify({
        ...parsed,
        hostId: normalized || undefined,
      });
      history.replace({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' });
    }
  };

  useEffect(() => {
    let cancelled = false;
    setHostsLoading(true);

    getHosts()
      .then(res => {
        if (cancelled) return;
        const hosts = (res && res.data) || [];
        let options = hosts.map(h => ({ value: String(h._id), label: h.name || String(h._id) }));

        if (!isAdmin && allowedHostIds.length > 0) {
          const allowed = allowedHostIds.map(String);
          options = options.filter(opt => allowed.includes(String(opt.value)));
        }

        setHostOptions(options);
        const preferred = resolvePreferredHostId({ options, allowedHostIds, queryHostId });
        if (preferred) {
          setHostIdState(String(preferred));
          setMarketplaceSelectedHostId(preferred);
        } else {
          setHostIdState(undefined);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (allowedHostIds[0]) {
          const fallback = String(queryHostId || allowedHostIds[0]);
          setHostIdState(fallback);
          setHostOptions([{ value: fallback, label: 'Current host' }]);
          setMarketplaceSelectedHostId(fallback);
        } else {
          setHostOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setHostsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, queryHostId]);

  return (
    <OwnerReportingHostContext.Provider value={{ hostId, setHostId, hostOptions, hostsLoading, isAdmin }}>
      {children}
    </OwnerReportingHostContext.Provider>
  );
};

export const OwnerReportingHostProvider = withRouter(OwnerReportingHostProviderInner);

export const useOwnerReportingHost = () => useContext(OwnerReportingHostContext);

export const OwnerReportingHostPicker = ({ style }) => {
  const { hostId, setHostId, hostOptions, hostsLoading } = useOwnerReportingHost();
  return (
    <Select
      showSearch
      optionFilterProp="children"
      loading={hostsLoading}
      placeholder="Select host"
      value={hostId}
      onChange={setHostId}
      style={style || { minWidth: 220 }}
    >
      {hostOptions.map(o => (
        <Select.Option key={o.value} value={o.value}>
          {o.label}
        </Select.Option>
      ))}
    </Select>
  );
};
